import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PageHero } from '@/components/layout/PageHero';

/**
 * Simulators index — picker for the simulator surfaces.
 *
 * Only the Submission Dossier Simulator is currently open. The Clinical
 * Protocol and Regulatory Strategy cards carry ``comingSoon``, which renders
 * them greyed with a disabled action — their routes still exist, so removing
 * the flag re-opens them. The card grid matches the legacy
 * ``simulators-list.html`` layout.
 */
export const Route = createFileRoute('/simulators')({
  component: SimulatorsListPage,
});

interface SimulatorEntry {
  title: string;
  badge: string;
  description: string;
  to?: string;
  comingSoon?: boolean;
}

const SIMULATORS: SimulatorEntry[] = [
  {
    title: 'Clinical Protocol Simulator',
    badge: 'Clinical',
    description:
      'Upload a draft clinical study protocol (.docx) and get a pre-screen report against 21 CFR 312 and ICH E6(R2) / E9 — critical, major, and minor issues flagged with regulation citations and fix recommendations.',
    to: '/simulators/clinical-protocol',
    comingSoon: true,
  },
  {
    title: 'Regulatory Strategy Reviewer',
    badge: 'Regulatory',
    description:
      'Upload a draft regulatory strategy (.docx or .pdf) and get a pre-screen against FDA pre-submission engagement expectations — Critical, Major, Minor findings with regulation citations.',
    to: '/simulators/regulatory-strategy',
    comingSoon: true,
  },
  {
    title: 'Submission Dossier Simulator',
    badge: 'Multi-country',
    description:
      'Upload a draft submission dossier (.pdf or .docx), pick the jurisdiction, and get an adversarial red-team pre-screen for likely rejection grounds — Critical / Major / Minor findings grounded in the selected country\'s regulatory record.',
    to: '/simulators/dossier',
  },
];

function SimulatorsListPage() {
  return (
    <>
      <PageHero
        title="Simulators"
        subtitle="Interactive pre-screening tools for regulatory and clinical submissions."
      />

      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pt-8 pb-20">
        <h2 className="text-navy text-lg font-bold mt-2 mb-4 tracking-tight uppercase">
          Available Simulators
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[22px]">
          {SIMULATORS.map((s) => (
            <SimulatorCard key={s.title} entry={s} />
          ))}
        </div>
      </main>
    </>
  );
}

function SimulatorCard({ entry }: { entry: SimulatorEntry }) {
  return (
    <article
      className={`
        relative bg-gradient-to-b from-white to-[#f6f8fc]
        border border-line rounded-2xl px-6 pt-6 pb-5
        flex flex-col items-start gap-3
        shadow-[0_1px_2px_rgba(20,40,70,0.04),0_6px_16px_rgba(20,40,70,0.05)]
        ${entry.comingSoon ? 'opacity-60' : ''}
      `}
    >
      <span
        className={`inline-flex items-center text-[10.5px] font-bold uppercase tracking-[0.12em] py-1 px-2 rounded-full ${
          entry.comingSoon
            ? 'bg-line text-muted'
            : 'bg-electric/15 text-navy border border-electric/30'
        }`}
      >
        {entry.badge}
      </span>
      <h3 className="font-sans font-bold text-navy text-[17px] leading-snug m-0">
        {entry.title}
      </h3>
      <p className="text-muted text-[13.5px] leading-[1.55] m-0 flex-1">
        {entry.description}
      </p>
      {entry.to && !entry.comingSoon ? (
        <Button variant="brand" asChild>
          <Link to={entry.to}>
            Open simulator <ArrowRight className="size-3.5" />
          </Link>
        </Button>
      ) : (
        <Button variant="brand" disabled>
          Open simulator
        </Button>
      )}
    </article>
  );
}
